import { useCallback, useEffect, useState } from "react";
import {
  CircularProgress,
  Typography,
  Box,
  Paper,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import {Link } from "@mui/material";
import { formatDateTimeWithSeconds } from "../../utils/DateTimeUtils";

//Services
import { SosAlertService } from "../../services/SosAlertService";

//Colors
import { colors } from "../../styles/colors";

//Common styles
import {
  BlueBorderContainer,
  CustomTableRowTR,
  FilledButton,
  HorizontalContainer,
  Paragraph,
  SectionTitle,
  ShowLabel,
  ShowValue,
  SixColTwoRowGrid,
  StyledTable,
  TableData,
  TableHeader,
  WorkspacePanel,
  WorkspaceSection,
  WorkSpaceTitleRow,
} from "../../styles/commonStyles";

//Svg Icons
import { AlertIcon } from "../Svg";
import { CommentIcon } from "../Svg/CommentIcon";

//Types
import { SosAlert } from "./sosAlert";

//Common Components
import SosUpdateDialog from "../Common/SosUpdateDialog/SosUpdateDialog";
import CustomCallIconButton from "../Common/CallButton/CustomCallIconButton";
import { UserType } from "../../enums/common";
import { maskPhoneNumber } from "../../utils/PhoneNumberUtils";

const sosAlertService = new SosAlertService();

const SosAlertMetaShow = ({ id }: { id: string }) => {
  const [data, setData] = useState<SosAlert | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await sosAlertService.getSosAlertById(Number(id));
      setData(response);
    } catch (err) {
      setError("Failed to load SOS Alert details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getStatusStyles = (status?: string) => {
    switch (status) {
      case "OPEN":
        return {
          color: "#1D4ED8",
          background: "rgba(37, 99, 235, 0.12)",
        };
      case "IN_PROGRESS":
        return {
          color: "#B45309",
          background: "rgba(245, 158, 11, 0.18)",
        };
      case "RESOLVED":
        return {
          color: "#047857",
          background: "rgba(16, 185, 129, 0.16)",
        };
      case "CLOSED":
        return {
          color: "#6B7280",
          background: "rgba(107, 114, 128, 0.16)",
        };
      default:
        return {
          color: "#6B7280",
          background: "rgba(107, 114, 128, 0.08)",
        };
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" mt={4}>
        {error}
      </Typography>
    );
  }

  if (!data) return null;

  return (
    <WorkspacePanel>
      <WorkspaceSection>
        <BlueBorderContainer>
          <WorkSpaceTitleRow>
            <div style={{backgroundColor: "rgba(25, 117, 210, 0.1)", padding: '10px', borderRadius: '12px'}}>
                <AlertIcon
              color={colors.primary}
              height={20}
              width={20}
            />
            </div>
            
            <SectionTitle>
              SOS Alert#{id}
            </SectionTitle>
          </WorkSpaceTitleRow>

          <Box display="flex" alignItems="center" gap={1.5}>
            <FilledButton onClick={() => setOpenUpdateDialog(true)}>
              <CommentIcon color="#fff" width={20} height={20} />
              Update Status / Add Comment
            </FilledButton>
          </Box>

        </BlueBorderContainer>
      </WorkspaceSection>

      <SixColTwoRowGrid>
        <div>
            <ShowLabel>Sos ID</ShowLabel>
            <Paragraph>{data?.id || "-"}</Paragraph>
        </div>

        <div>
            <ShowLabel>Reference ID {data?.referenceId || "-"}</ShowLabel>
            <Paragraph>

                <Link
                  href={`/#/${data?.refernceModelName === "ONE_MEAL_SERVICE" ? 'OneMealService' : 'ChefForParty'}/${data?.referenceId}/show`}
                  underline="hover"
                >
                  {data?.referenceId}
                </Link>
            </Paragraph>
        </div>

        <div>
            <ShowLabel>Reference Model Name</ShowLabel>
            <Paragraph>{data?.refernceModelName || "-"}</Paragraph>
        </div>

        <div>
            <ShowLabel>Query ID</ShowLabel>
            <Paragraph>{(data?.queryDetails as any)?.id || "-"}</Paragraph>
        </div>

        <div>
            <ShowLabel>Alert Reason</ShowLabel>
            <Paragraph>{(data?.queryDetails as any)?.trEn || "-"}</Paragraph>
        </div>

        <div>
            <ShowLabel>Query Description</ShowLabel>
            <Paragraph>{data?.queryDesc || "-"}</Paragraph>
        </div>

        <div>
            <ShowLabel>Cook</ShowLabel>
            <Paragraph>
              {data?.cookId ? (
                <Link href={`/#/Cooks/${data.cookId}/show`}>
                  {[data?.cookProfile?.firstName, data?.cookProfile?.lastName]
                    .filter(Boolean)
                    .join(" ") || `Cook #${data.cookId}`}
                </Link>
              ) : (
                "-"
              )}
            </Paragraph>
        </div>

        <div>
            <ShowLabel>Cook Phone</ShowLabel>
            <HorizontalContainer>
              {data?.cookProfile?.mobile ? (
                <CustomCallIconButton
                  color="#6C7C93"
                  size={18}
                  toPhoneNumber={data.cookProfile.mobile}
                  userType={UserType.COOK}
                  referenceId={Number(data?.referenceId)}
                  referenceModelName={data?.refernceModelName}
                />
              ) : null}
              <ShowValue style={{ marginBottom: 0 }}>
                {maskPhoneNumber(data?.cookProfile?.mobile || "") || "-"}
              </ShowValue>
            </HorizontalContainer>
        </div>

        <div>
            <ShowLabel>Alert Created At</ShowLabel>
            <Paragraph>{(data?.queryDetails as any)?.createdAt ? formatDateTimeWithSeconds((data?.queryDetails as any)?.createdAt) : "-"}</Paragraph>
        </div>

        <div>
            <ShowLabel>Status</ShowLabel>
            <Paragraph style={{...getStatusStyles(data?.status), display: 'inline-block', padding: '4px 20px' ,borderRadius: '4px'}}>{data?.status || "-"}</Paragraph>
        </div>

        <div>
          <ShowLabel>Status Last Updated At</ShowLabel>
          <Paragraph>{data?.lastUpdatedAt ? formatDateTimeWithSeconds(data?.lastUpdatedAt) : "-"}</Paragraph>
        </div>

        
          <div>
            <ShowLabel>Status Updated By</ShowLabel>
          {data?.lastUpdatedBy ? (
            <Link
              href={`/#/AdminUsers/${data?.lastUpdatedBy}/show`}
            >
            <Paragraph style={{color: colors.primaryDark}}>
              {data?.lastUpdatedBy || "-"}
            </Paragraph>
          </Link>
            
          
        ) : (<Paragraph style={{color: colors.primaryDark}}>
              {data?.lastUpdatedBy || "-"}
            </Paragraph>)
       }
       </div>
        

      </SixColTwoRowGrid>

      <Paragraph style={{ margin: "24px 0px 16px 0px", fontWeight: "bold" }}>
        Comments
      </Paragraph>

      <TableContainer component={Paper} sx={{ borderRadius: "8px" }}>
        <StyledTable>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "rgba(232, 235, 237, 0.3) !important",
              }}
            >
              <TableHeader>Comment</TableHeader>
              <TableHeader>Commenter email</TableHeader>
              <TableHeader>Timestamp</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.comments && data.comments.length > 0 ? (
              data.comments.map((each: any) => (
                <CustomTableRowTR key={each.id}>
                  <TableData>{each?.comment}</TableData>
                  <TableData>{each?.commenterEmail}</TableData>
                  <TableData>
                    {each?.timestamp
                      ? formatDateTimeWithSeconds(each?.timestamp)
                      : "-"}
                  </TableData>
                </CustomTableRowTR>
              ))
            ) : (
              <TableRow>
                <TableHeader colSpan={3} align="center">
                  No data available
                </TableHeader>
              </TableRow>
            )}
          </TableBody>
        </StyledTable>
      </TableContainer>


      <SosUpdateDialog
        open={openUpdateDialog}
        onClose={() => setOpenUpdateDialog(false)}
        sosId={Number(id)}
        currentStatus={data?.status}
        referenceModelName={data?.refernceModelName}
        alertReason={(data?.queryDetails as any)?.trEn}
        onSuccess={loadData}
      />

    </WorkspacePanel>
  );
};

export default SosAlertMetaShow;
